import React, { useState } from "react";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Portal,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import AllergenIngredientModal from "@/components/AllergenModal/AllergenIngredientModal";
import AllergenRecipesModal from "@/components/AllergenModal/AllergenRecipesModal";
import FruitsIcon from "@/components/Icons/FruitsIcon";
import InfoIcon from "@/components/Icons/InfoIcon";
import PlateCoverIcon from "@/components/Icons/PlateCoverIcon";
import { FoodSource } from "@/gql/graphql";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { colors } from "@/theme/overrides/colors";
import { stopPropagation } from "@/utils/functions";
import { percent, round } from "@/utils/number";

import { PlannedMealItem } from "../store/PlannedMealItem";

interface Props {
  item: PlannedMealItem;
  onEdit: (item: PlannedMealItem) => void;
}

const useStyles = makeStyles()((theme) => ({
  foodIcon: {
    color: colors.primary,
    backgroundColor: "rgba(31,212,175,0.1)", // #1FD4AF, 10% opacity
  },
  fruitIcon: {
    color: colors.secondary,
    backgroundColor: "rgba(255,157,55,0.1)", // #FF9D37, 10% opacity
  },
  infoIcon: {
    marginLeft: theme.spacing(2),
    fontSize: 16,
    color: "#C0C5CD",
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.gray,
    backgroundColor: "#E8EEF6",
  },
  textPrimary: {
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 600,
    color: colors.dark,
  },
  textSecondary: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 600,
    color: colors.gray,
  },
  epWeight: {
    background: theme.palette.grey["200"],
    borderRadius: "4px",
    padding: "2px 8px",
    marginLeft: theme.spacing(2),
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    minWidth: 98,
    maxWidth: 98,
  },
  amountButton: {
    padding: theme.spacing(0.5, 2),
    border: "1px solid #C0C5CD",
    borderRadius: 0,
    width: 98,
    maxWidth: 98,
    color: colors.dark,
    "& .MuiButton-label": {
      display: "inline-block",
      width: "100%",
      overflowX: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
  },
  warningIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px 4px 8px",
    backgroundColor: "#FF6262",
    borderRadius: 24,
  },
  warningIndicatorText: {
    fontSize: 8,
    fontWeight: 600,
    color: colors.white,
  },
  warningLogo: {
    width: 12,
    height: 12,
  },
  tooltipArrow: {
    color: colors.dark,
  },
  tooltipTooltip: {
    backgroundColor: colors.dark,
    borderRadius: 0,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 600,
    color: colors.white,
  },
  tooltipTextValue: {
    color: theme.palette.primary.main,
  },
  secondaryListItem: {
    display: "flex",
    alignItems: "center",
    marginTop: 4,
  },
}));

const PlannedMealItemsCellItem: React.FC<Props> = observer(
  ({ item, onEdit }) => {
    const { classes } = useStyles();

    const [allergenRecipesModal, setAllergenRecipesModal] = useState(false);
    const [allergenIngredientModal, setAllergenIngredientModal] =
      useState(false);

    const preventCellRipple = (e: React.MouseEvent) => {
      // Prevent buttons of this cell to trigger ripple effect of the whole cell
      e.stopPropagation();
    };

    useKeyboardShortcut("Ctrl+D", () => {
      if (item.cell.isActive) item.remove();
    });

    const macronutrientValue = (macro: number | null | undefined) => {
      if (macro == null) return "N/A";
      const percentage = percent(macro / item.food.epWeightGrams, { round: 0 });
      return `${round(macro * item.weightPercent, 2)}g(${percentage}%)`;
    };

    const icon = (() => {
      switch (item.food.category) {
        case "Fresh Fruits":
          return (
            <Avatar className={classes.fruitIcon}>
              <FruitsIcon htmlColor={colors.gray} />
            </Avatar>
          );
        default:
          return (
            <Avatar className={classes.foodIcon}>
              <PlateCoverIcon htmlColor={colors.gray} />
            </Avatar>
          );
      }
    })();
    return (
      <ListItem disableGutters>
        <ListItemAvatar>{icon}</ListItemAvatar>
        <ListItemText
          primary={
            <>
              <Box display="flex" alignItems="center" gap="10px">
                {item.food.name}
                {item.hasAllergies && (
                  <div
                    className={classes.warningIndicator}
                    onClick={() => {
                      if (
                        item.food.source ==
                        FoodSource.Recipes.toLocaleLowerCase()
                      ) {
                        setAllergenRecipesModal(true);
                      } else {
                        setAllergenIngredientModal(true);
                      }
                    }}
                  >
                    <WarningIcon
                      className={classes.warningLogo}
                      htmlColor={colors.white}
                    />
                    <Typography className={classes.warningIndicatorText}>
                      Allergen
                    </Typography>
                  </div>
                )}
              </Box>
            </>
          }
          secondary={
            <div className={classes.secondaryListItem}>
              <span>{Math.round(item.energyKcal)} kcal</span>
              <span className={classes.epWeight}>
                {round(item.weightGrams, 2)} g
              </span>
              <Tooltip
                arrow
                classes={{
                  arrow: classes.tooltipArrow,
                  tooltip: classes.tooltipTooltip,
                }}
                title={
                  <div>
                    <Typography className={classes.tooltipText}>
                      Proteins :{" "}
                      <span className={classes.tooltipTextValue}>
                        {macronutrientValue(item.food.proteinGrams)}
                      </span>
                      , Fats :{" "}
                      <span className={classes.tooltipTextValue}>
                        {macronutrientValue(item.food.fatGrams)}
                      </span>
                      ,<br /> Carbohydrates :{" "}
                      <span className={classes.tooltipTextValue}>
                        {macronutrientValue(item.food.carbohydrateGrams)}
                      </span>
                    </Typography>
                    <br />
                    <Typography className={classes.tooltipText}>
                      Source: {item.food.source}
                    </Typography>
                  </div>
                }
              >
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            </div>
          }
          classes={{
            primary: classes.textPrimary,
            secondary: classes.textSecondary,
          }}
        />
        <Portal>
          <AllergenRecipesModal
            foodId={item.food.id}
            fromAdded={true}
            visibility={allergenRecipesModal}
            onAddRecipe={() => {
              setAllergenRecipesModal(false);
            }}
            onRemoveRecipe={() => item.remove()}
            onClose={() => setAllergenRecipesModal(false)}
          />
          <AllergenIngredientModal
            foodId={item.food.id}
            fromAdded={true}
            visibility={allergenIngredientModal}
            onAddIngredient={() => {
              setAllergenIngredientModal(false);
            }}
            onRemoveIngredient={() => item.remove()}
            onClose={() => setAllergenIngredientModal(false)}
          />
        </Portal>
        <Box className={classes.controls}>
          {item.cell.isDeleting ? (
            <IconButton
              onMouseDown={preventCellRipple}
              onClick={stopPropagation(() => item.remove())}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Tooltip
                title={`${round(item.amount, 2)} ${
                  item.foodPortion.unitWithModifier
                }`}
                placement="top"
              >
                <Button
                  className={classes.amountButton}
                  fullWidth
                  size="small"
                  variant="text"
                  onMouseDown={preventCellRipple}
                  onClick={stopPropagation(() => onEdit(item))}
                >
                  {`${round(item.amount, 2)} ${
                    item.foodPortion.unitWithModifier
                  }`}
                </Button>
              </Tooltip>
              <Box
                width="100%"
                marginTop="2px"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <IconButton
                  size="small"
                  onMouseDown={preventCellRipple}
                  onClick={stopPropagation(() => item.decrementAmount())}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onMouseDown={preventCellRipple}
                  onClick={stopPropagation(() => item.incrementAmount())}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </ListItem>
    );
  }
);

export default PlannedMealItemsCellItem;
